import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { PurchaseOrderProps } from "@/components/campaign/types/purchaseOrderV2";
import { usePurchaseOrderV2 } from "../../../../hooks/campaign/usePurchaseOrderV2";
import { ReviewStep } from "./steps/ReviewStep";
import { SignatureStep } from "./steps/SignatureStep";
import { DocumentsStep } from "./steps/DocumentsStep";
import { SubmitStep } from "./steps/SubmitStep";
import { useRef, useEffect } from "react";

export const PurchaseOrderWizard = ({
  open,
  onOpenChange,
  onComplete,
  campaignId,
  companyDetails,
  devices,
  duration,
  amount,
  onBackToPaymentPlan
}: PurchaseOrderProps & { onBackToPaymentPlan?: () => void }) => {
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  
  const {
    currentStep,
    steps,
    validation,
    isSubmitting,
    purchaseOrderData,
    nextStep,
    previousStep,
    goToStep,
    canGoNext,
    canGoPrevious,
    isLastStep,
    progress,
    updatePurchaseOrderData,
    handleSignatureSave,
    handleStampUpload,
    handleStampRemove,
    handleFileUpload,
    submitPurchaseOrder
  } = usePurchaseOrderV2(campaignId, companyDetails, devices, duration, amount, onComplete);

  // Scroll to top when step changes
  useEffect(() => {
    if (scrollAreaRef.current) {
      const scrollContainer = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollContainer) {
        scrollContainer.scrollTop = 0;
      }
    }
  }, [currentStep]);

  const renderCurrentStep = () => {
    switch (steps[currentStep]?.id) {
      case 'review':
        return (
          <ReviewStep
            purchaseOrderData={purchaseOrderData}
            validation={validation}
            onUpdate={updatePurchaseOrderData}
          />
        );
      case 'signature':
        return (
          <SignatureStep
            purchaseOrderData={purchaseOrderData}
            validation={validation}
            onSignatureSave={handleSignatureSave}
            onStampUpload={handleStampUpload}
            onStampRemove={handleStampRemove}
          />
        );
      case 'documents':
        return (
          <DocumentsStep
            purchaseOrderData={purchaseOrderData}
            validation={validation}
            onFileUpload={handleFileUpload}
          />
        );
      case 'submit':
        return (
          <SubmitStep
            purchaseOrderData={purchaseOrderData}
            onSubmit={submitPurchaseOrder}
            isSubmitting={isSubmitting}
          />
        );
      default:
        return null;
    }
  };

  const handleNext = () => {
    if (isLastStep) {
      submitPurchaseOrder();
    } else {
      nextStep();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl h-[90vh] p-0 flex flex-col">
        <DialogHeader className="px-6 pr-12 py-4 border-b">
          <DialogTitle className="flex items-center justify-between">
            <span>Purchase Order - {purchaseOrderData.poNumber}</span>
            <div className="text-sm text-muted-foreground">
              Step {currentStep + 1} of {steps.length}
            </div>
          </DialogTitle>
        </DialogHeader>

        {/* Progress Bar */}
        <div className="px-6 py-2">
          <Progress value={progress} className="h-2" />
        </div>

        {/* Step Navigation */}
        <div className="px-6 py-2 border-b">
          <div className="flex items-center space-x-4 overflow-x-auto">
            {steps.map((step, index) => (
              <button
                key={step.id}
                onClick={() => goToStep(index)}
                className={cn(
                  "flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap",
                  index === currentStep && "bg-primary text-primary-foreground",
                  index < currentStep && "bg-green-100 text-green-700 hover:bg-green-200",
                  index > currentStep && canGoNext && "hover:bg-muted",
                  index > currentStep && !canGoNext && "opacity-50 cursor-not-allowed"
                )}
                disabled={index > currentStep && !canGoNext}
              >
                {step.isComplete ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <span className="w-4 h-4 rounded-full border-2 border-current flex items-center justify-center text-xs">
                    {index + 1}
                  </span>
                )}
                <span>{step.title}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Current Step Content */}
        <ScrollArea ref={scrollAreaRef} className="flex-1 px-6">
          <div className="py-6">
            <div className="mb-6">
              <h2 className="text-xl font-semibold">{steps[currentStep]?.title}</h2>
              <p className="text-muted-foreground mt-1">{steps[currentStep]?.description}</p>
            </div>

            {/* Validation Errors */}
            {validation.errors.length > 0 && (
              <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                <h3 className="font-medium text-destructive mb-2">Please fix the following issues:</h3>
                <ul className="list-disc list-inside space-y-1 text-sm text-destructive">
                  {validation.errors.map((error, index) => (
                    <li key={index}>{error.message}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Validation Warnings */}
            {validation.warnings.length > 0 && (
              <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <h3 className="font-medium text-yellow-800 mb-2">Warnings:</h3>
                <ul className="list-disc list-inside space-y-1 text-sm text-yellow-700">
                  {validation.warnings.map((warning, index) => (
                    <li key={index}>{warning}</li>
                  ))}
                </ul>
              </div>
            )}

            {renderCurrentStep()}
          </div>
        </ScrollArea>

        {/* Footer Actions */}
        <div className="px-6 py-4 border-t flex items-center justify-between">
          <Button
            variant="outline"
            onClick={() => {
              if (currentStep === 0 && onBackToPaymentPlan) {
                onBackToPaymentPlan();
              } else {
                previousStep();
              }
            }}
            disabled={isSubmitting}
            className="flex items-center space-x-2"
          >
            <ChevronLeft className="h-4 w-4" />
            <span>Previous</span>
          </Button>

          <div className="flex items-center space-x-2">
            <Button
              onClick={handleNext}
              disabled={isSubmitting}
              className={cn(
                "flex items-center space-x-2",
                isLastStep && "bg-green-600 hover:bg-green-700"
              )}
            >
              <span>{isLastStep ? 'Submit Order' : 'Next'}</span>
              {!isLastStep && <ChevronRight className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};